import { PromotionPerformanceMetricInput, PromotionPerformanceMetricRow } from "../../../../../api/promotion_effectiveness"
import { Promotion } from "../../../promo_creation/types"

export type PerformanceEditorFocus = "sales" | "margin" | "all"

export function is_manual_metric_row(row: PromotionPerformanceMetricRow): boolean {
  return row.source_type === "manual_upload"
}

export function default_metric_input(promotion: Promotion): PromotionPerformanceMetricInput {
  const product = promotion.products?.[0] ?? null
  return {
    period_start: promotion.start_date ?? null,
    period_end: promotion.end_date ?? promotion.start_date ?? null,
    customer: promotion.customer ?? null,
    retailer: promotion.retailer ?? null,
    product,
    market: null,
    total_sales_dollars: null,
    baseline_sales_dollars: null,
    incremental_sales_dollars: null,
    total_units: null,
    baseline_units: null,
    incremental_units: null,
    incremental_margin_dollars: null,
    gross_margin_pct: null,
    acv: null,
    tdp: null,
    promo_price: null,
    regular_price: null,
    discount_depth_pct: null,
    feature_support: false,
    display_support: false,
    notes: null,
  }
}

export function metric_row_to_input(row: PromotionPerformanceMetricRow): PromotionPerformanceMetricInput {
  return {
    period_start: row.period_start,
    period_end: row.period_end,
    customer: row.customer ?? null,
    retailer: row.retailer ?? null,
    product: row.product ?? null,
    market: row.market ?? null,
    total_sales_dollars: row.total_sales_dollars ?? null,
    baseline_sales_dollars: row.baseline_sales_dollars ?? null,
    incremental_sales_dollars: row.incremental_sales_dollars ?? null,
    total_units: row.total_units ?? null,
    baseline_units: row.baseline_units ?? null,
    incremental_units: row.incremental_units ?? null,
    incremental_margin_dollars: row.incremental_margin_dollars ?? null,
    gross_margin_pct: row.gross_margin_pct ?? null,
    acv: row.acv ?? null,
    tdp: row.tdp ?? null,
    promo_price: row.promo_price ?? null,
    regular_price: row.regular_price ?? null,
    discount_depth_pct: row.discount_depth_pct ?? null,
    feature_support: row.feature_support ?? false,
    display_support: row.display_support ?? false,
    notes: row.notes ?? null,
  }
}

export function parse_money_input(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const normalized = trimmed.replace(/[$,]/g, "")
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

export function format_money_input(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return ""
  return String(value)
}

export function focus_fields_for_editor(focus: PerformanceEditorFocus): string[] {
  switch (focus) {
    case "sales":
      return ["baseline_sales_dollars", "incremental_sales_dollars", "total_sales_dollars"]
    case "margin":
      return ["incremental_margin_dollars"]
    default:
      return []
  }
}

export function editor_focus_label(focus: PerformanceEditorFocus): string {
  switch (focus) {
    case "sales":
      return "Sales and lift metrics"
    case "margin":
      return "Margin metrics"
    default:
      return "Performance metrics"
  }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("promotion_performance_editor_utils", () => {
    it("defaults metric input from promotion context", () => {
      const input = default_metric_input({
        id: "p1",
        name: "Demo",
        products: ["Sparkling Water"],
        customer: "KeHE",
        retailer: "Sprouts",
        start_date: "2026-01-01",
        end_date: "2026-01-31",
      })
      expect(input.product).toBe("Sparkling Water")
      expect(input.period_start).toBe("2026-01-01")
      expect(input.period_end).toBe("2026-01-31")
    })

    it("parses money input safely", () => {
      expect(parse_money_input("$1,234.50")).toBe(1234.5)
      expect(parse_money_input("")).toBeNull()
    })

    it("identifies manual metric rows", () => {
      expect(is_manual_metric_row({ id: "1", promotion_id: "p", source_type: "manual_upload", source_row_ids: [], period_start: "2026-01-01", period_end: "2026-01-31" })).toBe(true)
      expect(is_manual_metric_row({ id: "1", promotion_id: "p", source_type: "syndicated_report", source_row_ids: [], period_start: "2026-01-01", period_end: "2026-01-31" })).toBe(false)
    })
  })
}
